import { formatValue, UploadedFile } from "@smart/bridge-types-basic";
import { Displayer, RequiredAsterisk } from "@smart/itops-sb-design-system-dom";
import { specialChars } from "@smart/itops-utils-basic";

import { SummaryAppointment } from "./summary-appointment";
import { SummaryPayment } from "./summary-payment";
import { FieldComponentType, fieldFallbackLabel, FieldItem } from "../../types";

export type SummaryFieldProps = {
  field: FieldItem;
  value: any;
  index?: number;
};

export const SummaryField = ({ field, value, index }: SummaryFieldProps) => {
  const { type, options, label, mandatory, field: layoutField } = field;

  const displayFieldValue = () => {
    if (type === "file" && Array.isArray(value)) {
      const uploadedFiles = value?.filter(
        (file: UploadedFile) => file.uploadStatus === "uploaded",
      );
      const dataTestId = `file-field-item-${field.uri}`;
      return uploadedFiles?.length ? (
        uploadedFiles.map((file: UploadedFile) => (
          <p
            key={file.downloadUrl}
            className="text-ui"
            data-testid={dataTestId}
          >
            {file.fileName}
          </p>
        ))
      ) : (
        <p className="text-ui" data-testid={dataTestId}>
          {specialChars.enDash}
        </p>
      );
    }

    if (type === "payment")
      return <SummaryPayment response={value} fieldUri={field.uri} />;

    if (type === "multilineText") {
      const paragraphs = formatValue(value, type, options)
        ?.trim()
        .split("\n\n")
        .filter(Boolean);

      return paragraphs?.length ? (
        paragraphs.map((text) => (
          <p key={text} className="text-ui">
            {text}
          </p>
        ))
      ) : (
        <p className="text-ui">
          <span>{specialChars.enDash}</span>
        </p>
      );
    }

    if (type === "checkbox" || type === "choice") {
      const values = Array.isArray(value) ? value : [value];
      const filteredValues = values.filter(Boolean);

      const getValue = (v: string) => {
        const valueInOptions = options?.find((o) => o.value === v);
        if (valueInOptions) {
          return valueInOptions.label;
        }
        // There is no "Other" options for field with dataSourceMapping
        return layoutField?.dataSourceMapping ? v : `Other - ${v}`;
      };

      return !filteredValues.length ? (
        <p className="text-ui">
          <span>{specialChars.enDash}</span>
        </p>
      ) : (
        filteredValues.map((v) => (
          <p key={v} className="text-ui">
            {getValue(v)}
          </p>
        ))
      );
    }

    const formatted = formatValue(value, type, options).trim();
    return (
      <div className="text-ui">
        <span>{formatted || specialChars.enDash}</span>
      </div>
    );
  };

  const isInvalidPaymentField =
    type === "payment" &&
    (!value || !field.payment?.amountInCents || !field.payment.bankAccount);
  if (type === "info" || isInvalidPaymentField) return null;

  if (type === "appointment") {
    return <SummaryAppointment field={field} response={value} />;
  }

  return (
    <div
      key={index === undefined ? field.uri : `${field.uri}-${index}`}
      className="flex flex-col gap-small print:break-inside-avoid hyphens-auto"
      data-testid={`summary-field-wrapper-${field.uri}`}
    >
      <div className="flex flex-col gap-xSmall">
        <div className="[&_h3]:text-ui [&_h3]:font-semibold">
          <Displayer
            paragraphTag="h3"
            value={label}
            fallback={fieldFallbackLabel[type as FieldComponentType]}
            hintElement={mandatory && <RequiredAsterisk />}
          />
        </div>
      </div>
      {displayFieldValue()}
    </div>
  );
};
